import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/LandingPage.css';
import DemoRequestModal from '../components/DemoRequestModal';
import ContactModal from '../components/modal/ContactModal';

const LandingPage = () => {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(0);
  const [progressWidth, setProgressWidth] = useState(0);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const sections = ['hero', 'features', 'alerts', 'cta'];
  const sectionLabels = ['Home', 'Features', 'Alerts', 'Get Started'];

  const handleSignIn = () => {
    navigate('/login');
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgressWidth((scrolled / total) * 100);

      for (let i = 0; i < sections.length; i++) {
        const section = document.getElementById(sections[i]);
        if (section) {
          const rect = section.getBoundingClientRect();
          if (rect.top <= window.innerHeight * 0.4 && rect.bottom >= window.innerHeight * 0.4) {
            if (currentSection !== i) setCurrentSection(i);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentSection, sections]);

  return (
    <>
      {/* Progress Bar */}
      <div className="progress-bar" style={{ width: `${progressWidth}%` }}></div>

      {/* Navigation */}
      <nav className="nav">
        <div className="nav-left">
          <div className="logo">
            <div className="logo-badge">✈</div>
            <div>
              <div className="logo-name">aero<span>Trick</span></div>
              <span className="logo-sub">FBO Management</span>
            </div>
          </div>
        </div>
        <div className="nav-center">
          <a onClick={() => scrollToSection('features')}>Features</a>
          <a onClick={() => scrollToSection('alerts')}>Alerts</a>
          <a onClick={() => scrollToSection('cta')}>Pricing</a>
          <a onClick={() => scrollToSection('cta')}>About</a>
        </div>
        <div className="nav-right">
          <div className="auth-btns">
            <button className="btn-signin" onClick={handleSignIn}>Sign In</button>
          </div>
          </div>
      </nav>

      {/* Scroll Navigation Dots */}
      <div className="scroll-nav">
        {sections.map((section, idx) => (
          <div
            key={section}
            className={`scroll-dot ${currentSection === idx ? 'active' : ''}`}
            data-label={sectionLabels[idx]}
            onClick={() => scrollToSection(section)}
          ></div>
        ))}
      </div>

      {/* Bottom Navigation */}
      <div className="scroll-bottom-nav">
        {sections.map((section, idx) => (
          <div
            key={section}
            className={`sbn-dot ${currentSection === idx ? 'active' : ''}`}
            onClick={() => scrollToSection(section)}
          >
            {String(idx + 1).padStart(2, '0')}
          </div>
        ))}
        <div className="sbn-label">{sectionLabels[currentSection]}</div>
      </div>

      {/* Hero Section */}
      <section id="hero">
        <div className="hero-container">
          <div className="hero-glow"></div>
          <div className="hero-glow2"></div>
          <div className="hero-left">
            <div className="live-badge">
              <div className="live-dot"></div>
              <span>Live traffic monitoring active</span>
            </div>
            <h1>Airport operations,<br /><em>fully automated.</em></h1>
            <p className="hero-desc">From takeoff detection and automated billing to staff scheduling, fuel management, and real-time emergency alerts — AeroTrick is the single platform your airport runs on.</p>
            <div className="hero-btns">
              <button className="btn-cta" onClick={() => scrollToSection('features')}>Explore Features ↗</button>
              <button className="btn-ghost" onClick={() => setIsDemoModalOpen(true)}>Request a Demo</button>
            </div>
          </div>
          <div className="hero-right">
            {/* Dashboard Preview */}
            <div className="dash-preview">
              <div className="dp-header">
                <div className="dp-title">aeroTrick <span className="dp-title-sub">Dashboard</span></div>
                <div className="dp-wifi">
                  <div className="live-dot-small"></div> 7 aircraft
                </div>
              </div>
              <div className="dp-stats">
                <div className="dp-stat">
                  <div className="dp-stat-left">
                    <p>Flights Today</p>
                    <p>24</p>
                  </div>
                  <div className="dp-stat-icon ic-blue">✈</div>
                </div>
                <div className="dp-stat">
                  <div className="dp-stat-left">
                    <p>Fuel (gal)</p>
                    <p className="text-orange">480</p>
                  </div>
                  <div className="dp-stat-icon ic-orange">⛽</div>
                </div>
                <div className="dp-stat">
                  <div className="dp-stat-left">
                    <p>Revenue</p>
                    <p className="text-green">$4.2k</p>
                  </div>
                  <div className="dp-stat-icon ic-green">$</div>
                </div>
                <div className="dp-stat">
                  <div className="dp-stat-left">
                    <p>Leases</p>
                    <p className="text-purple">12</p>
                  </div>
                  <div className="dp-stat-icon ic-purple">🏢</div>
                </div>
              </div>
              <div className="dp-map">
                <div className="map-grid"></div>
                <div className="map-ring"></div>
                <div className="map-ring"></div>
                <div className="map-ring"></div>
                <div className="map-center"></div>
                <div className="plane-dot yellow"></div>
                <div className="plane-dot cyan"></div>
                <div className="plane-dot orange"></div>
                <div className="map-label-bottom">⊕ KBOE — Airport</div>
                <div className="map-label-top">Live Radar</div>
              </div>
              <div className="dp-activity">
                <div className="dp-act-title">Recent Activity</div>
                <div className="dp-act-row">
                  <div className="dar-left">
                    <span className="arrow-down">↓</span>
                    <span className="dar-call">N5230K</span>
                    <span className="dar-type"> — Landing</span>
                  </div>
                  <div>
                    <span className="time">14:32</span>
                    <span className="dar-badge billed">Billed</span>
                  </div>
                </div>
                <div className="dp-act-row">
                  <div className="dar-left">
                    <span className="arrow-up">↑</span>
                    <span className="dar-call">N88701</span>
                    <span className="dar-type"> — Takeoff</span>
                  </div>
                  <div>
                    <span className="time">14:45</span>
                    <span className="dar-badge pending">Pending</span>
                  </div>
                </div>
                <div className="dp-act-row">
                  <div className="dar-left">
                    <span className="arrow-down">↓</span>
                    <span className="dar-call">N4417X</span>
                    <span className="dar-type"> — Landing</span>
                  </div>
                  <div>
                    <span className="time">14:58</span>
                    <span className="dar-badge billed">Billed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="stats-bar">
          <div className="sb-item">
            <div className="sb-val">100%</div>
            <div className="sb-lbl">Automated movement billing</div>
          </div>
          <div className="sb-item">
            <div className="sb-val">&lt;30s</div>
            <div className="sb-lbl">Alert delivery time</div>
          </div>
          <div className="sb-item">
            <div className="sb-val">24/7</div>
            <div className="sb-lbl">Live traffic visibility</div>
          </div>
          <div className="sb-item">
            <div className="sb-val">1 app</div>
            <div className="sb-lbl">Staff, ops & clients unified</div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features">
        <div className="section-tag">Core Modules</div>
        <div className="section-title">Everything your FBO needs</div>
        <p className="section-sub">AeroTrick covers the full operational cycle — each module connects seamlessly with the others so nothing slips through the cracks.</p>
        <div className="features-grid">
          <div className="feat-card"><div className="feat-icon fi-blue">✈</div><div className="feat-title">Movement Detection & Billing</div><div className="feat-desc">Auto-detect takeoffs and landings, generate invoices instantly. Every movement captured and billed without manual input.</div></div>
          <div className="feat-card"><div className="feat-icon fi-orange">⛽</div><div className="feat-title">Fuel Management</div><div className="feat-desc">Track stock levels, process client fuel orders, manage replenishment, and generate fuel invoices with full traceability.</div></div>
          <div className="feat-card"><div className="feat-icon fi-yellow">📅</div><div className="feat-title">Staff Scheduling</div><div className="feat-desc">Drag-and-drop planning, shift management, and workload tracking for your entire ground operations crew.</div></div>
          <div className="feat-card"><div className="feat-icon fi-cyan">🗺</div><div className="feat-title">Live Traffic Map</div><div className="feat-desc">Real-time radar-style view of your airport area. Track aircraft positions, callsigns, speed, and altitude live.</div></div>
          <div className="feat-card"><div className="feat-icon fi-pink">💬</div><div className="feat-title">Staff & Client Chat</div><div className="feat-desc">Built-in messaging between staff and clients. All communications logged and searchable in one place.</div></div>
          <div className="feat-card"><div className="feat-icon fi-green">🔧</div><div className="feat-title">Appointments & Checking</div><div className="feat-desc">Client self-service booking for fuel, maintenance, and pre-flight checks. Includes preparation checklists.</div></div>
          <div className="feat-card"><div className="feat-icon fi-purple">🚗</div><div className="feat-title">Fleet Management</div><div className="feat-desc">Track ground vehicles — status, availability, maintenance history, and usage logs for every unit in your fleet.</div></div>
          <div className="feat-card"><div className="feat-icon fi-red">🧾</div><div className="feat-title">Invoicing & Hangars</div><div className="feat-desc">Full billing for landing fees, fueling, services, and hangar leases. Export-ready reports and lease management.</div></div>
        </div>
      </section>

      {/* Alerts Section */}
      <section id="alerts">
        <div className="alert-inner">
          <div className="alert-text">
            <div className="section-tag">Real-Time Alerts</div>
            <div className="section-title">Push the right info,<br />to the right people.</div>
            <p className="alert-description">Traffic updates, emergency notifications, and operational alerts delivered across all major channels the moment they happen. Apple notifications coming soon.</p>
            <div className="alert-channels">
              <div className="ch-pill">✉ Email</div>
              <div className="ch-pill">📱 SMS</div>
              <div className="ch-pill">✈ Telegram</div>
              <div className="ch-pill soon">🍎 iPhone (soon)</div>
            </div>
          </div>
          <div className="alert-widget">
            <div className="aw-header">
              <div className="aw-title">🔔 Alerts &amp; Notifications <span className="aw-count">3</span></div>
              <span className="live-badge-small">● Live</span>
            </div>
            <div className="aw-item"><div className="aw-dot red"></div><div><div className="aw-msg">Emergency declared — KBOE RWY 14. All ground units standby.</div><div className="aw-meta"><span>14:31</span><span className="aw-channel">SMS</span><span className="aw-channel">Email</span><span className="aw-channel">Telegram</span></div></div></div>
            <div className="aw-item"><div className="aw-dot amber"></div><div><div className="aw-msg">RWY 32 closed — FOD inspection in progress. ETA 20 min.</div><div className="aw-meta"><span>13:58</span><span className="aw-channel">Email</span><span className="aw-channel">Telegram</span></div></div></div>
            <div className="aw-item"><div className="aw-dot green"></div><div><div className="aw-msg">Fuel appointment confirmed — N5230K, Apr 14 at 09:00, Gate 3.</div><div className="aw-meta"><span>13:15</span><span className="aw-channel">SMS</span><span className="aw-channel">Email</span></div></div></div>
          </div>
        </div>
      </section>

      {/* CTA Section & Footer */}
      <section id="cta">
        <div className="cta-tag">Get Started</div>
        <div className="cta-title">Ready to run a smarter airport?</div>
        <p className="cta-sub">Join the FBOs already operating with AeroTrick. Request a personalized demo and see results from day one.</p>
        <div className="cta-btns">
          <button className="btn-cta cta-large" onClick={() => setIsDemoModalOpen(true)}>Request a Demo ↗</button>
          <button className="btn-ghost cta-large">See Pricing</button>
        </div>
        <div className="footer">
          <p>© 2026 AeroTrick — Airport Management Solutions · aerotrick.com</p>
          <div className="footer-links">
            <a onClick={() => scrollToSection('features')}>Features</a>
            <a onClick={() => setShowContactModal(true)}>Contact</a>
            <a>Privacy Policy</a>
          </div>
        </div>
      </section>

      {/* Demo Request Modal */}
      <DemoRequestModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />

      {/* Contact Modal */}
      <ContactModal 
        isOpen={showContactModal} 
        onClose={() => setShowContactModal(false)} 
      />
    </>
  );
};

export default LandingPage;